import { LogOut, Settings, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router";
import { LanguageSwitcher } from "./language-switcher";
import { HelpButton } from "./help-dialog";
import { ThemeToggle } from "./theme-toggle";
import { WorkspaceSwitcher } from "./workspace-switcher";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-context";
import { env } from "@/lib/core/env";
import { initials } from "@/lib/core/format";
import { cn } from "@/lib/core/utils";
import { usePlan } from "@/hooks/use-plan";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
  );

export function SiteHeader() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="container mx-auto flex h-14 max-w-7xl items-center gap-4 px-4">
        <Link
          to="/dashboard"
          className="flex shrink-0 items-center gap-2 font-semibold"
          data-testid="brand"
        >
          <img
            src="/blanqo-logo.svg"
            alt={env.appName}
            className="h-6 w-auto dark:hue-rotate-180 dark:invert"
          />
        </Link>

        {isAuthenticated && <WorkspaceSwitcher />}

        {isAuthenticated && (
          // Phones use the bottom tab bar (components/layout/mobile-nav.tsx) instead.
          <nav
            className="hidden items-center gap-1 overflow-x-auto md:flex"
            aria-label={t("nav.main")}
          >
            <NavLink to="/dashboard" className={navLinkClass}>
              {t("nav.dashboard")}
            </NavLink>
            <NavLink to="/invoices" className={navLinkClass} data-testid="nav-invoices">
              {t("nav.invoices")}
            </NavLink>
            <NavLink to="/quotes" className={navLinkClass} data-testid="nav-quotes">
              {t("nav.quotes")}
            </NavLink>
            <NavLink to="/templates" className={navLinkClass} data-testid="nav-templates">
              {t("nav.templates")}
            </NavLink>
            <NavLink to="/customers" className={navLinkClass} data-testid="nav-customers">
              {t("nav.customers")}
            </NavLink>
            <NavLink to="/assistant" className={navLinkClass}>
              {t("nav.assistant")}
            </NavLink>
          </nav>
        )}

        <div className="ml-auto flex items-center gap-2">
          {isAuthenticated && <HeaderUpgrade />}
          {/* Signed in, the language is a profile setting (Settings, or the phone's "More"
              sheet); on a phone the theme lives there too. */}
          <div className={cn("items-center gap-2", isAuthenticated ? "hidden md:flex" : "flex")}>
            {!isAuthenticated && <LanguageSwitcher />}
            {isAuthenticated && <HelpButton />}
            <ThemeToggle />
          </div>
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  data-testid="user-menu"
                >
                  <Avatar className="size-8">
                    <AvatarFallback>{initials(user.name || user.email)}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel data-testid="user-name">
                  {user.name || user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="settings-link">
                  <Link to="/settings">
                    <Settings /> {t("nav.settings")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout"
                  onClick={() => {
                    // Leave the protected page first so its guard does not redirect to /login.
                    navigate("/login", { replace: true });
                    logout();
                  }}
                >
                  <LogOut /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" data-testid="login-link">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

/** A quiet "Upgrade" next to the menus, until the workspace is on Pro. */
function HeaderUpgrade() {
  const { t } = useTranslation();
  const { plan, isLoading } = usePlan();
  if (isLoading || plan === "pro") return null;
  return (
    <Button asChild variant="brand" size="sm" className="hidden sm:inline-flex">
      <Link
        to={`/settings/plans?highlight=${plan === "free" ? "starter" : "pro"}`}
        data-testid="header-upgrade"
      >
        <Sparkles /> {t("upgrade.header")}
      </Link>
    </Button>
  );
}
